import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { RITUALS, RITUAL_CATEGORIES } from "@/data/rituals";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";

const TRAD_COLORS = "border-[#A8722A]/40 text-[#A8722A]";

export default function RitualsHub() {
  const [trad, setTrad] = useState("All");
  const traditions = useMemo(() => ["All", ...Array.from(new Set(RITUALS.flatMap((r) => r.tradition)))], []);
  const filtered = (cat) => RITUALS.filter((r) => r.cat === cat && (trad === "All" || r.tradition.includes(trad)));

  useSeo({
    title: "Rituals & Vratas — ದೈನಂದಿನ ಕರ್ಮ ಮತ್ತು ವ್ರತಗಳು | Sandhyavandane, Upakarma, South Indian Vratas | PurohithConnect",
    description: "Learner-friendly step-by-step guides to Sandhyavandane (all 4 Vedas, morning/noon/evening), Upakarma, and South Indian vratas & poojas — Mangala Gowri, Varamahalakshmi, Gowri-Ganesha, Ananta, Durga, Lakshmi, Tulasi, Satyanarayana and more. Smartha, Madhwa, Srivaishnava, Iyer & Iyengar traditions.",
    canonicalPath: "/rituals",
    jsonLd: { "@context": "https://schema.org", "@type": "ItemList", name: "Rituals & Vratas", itemListElement: RITUALS.map((r, i) => ({ "@type": "ListItem", position: i + 1, name: r.title.en })) },
  });

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]">
      <SiteHeader />
      <section className="mx-auto max-w-6xl px-4 pt-12 md:px-8">
        <p className="font-baloo text-lg text-[#B23A2B]">ದೈನಂದಿನ ಕರ್ಮ ಮತ್ತು ವ್ರತಗಳು</p>
        <h1 className="mt-1 font-eczar text-4xl font-bold sm:text-5xl" data-testid="rituals-title">Rituals &amp; Vratas</h1>
        <p className="mt-3 max-w-3xl font-mukta text-base text-[#6B5044]">
          Simple, step-by-step guides for learners — Sandhyavandane for all four Vedas (morning, noon and evening), Upakarma, and the vratas &amp; poojas of South Indian homes. Each guide shows what to do and why. Full shakha-specific mantras are best learned from your guru or purohith.
        </p>
        <div className="mt-6 flex flex-wrap gap-2" data-testid="rituals-tradition-filter">
          {traditions.map((t) => (
            <button key={t} onClick={() => setTrad(t)} data-testid={`filter-${t.toLowerCase()}`}
              className={`rounded-full border px-4 py-1.5 font-mukta text-sm font-medium transition-colors ${trad === t ? "border-[#B23A2B] bg-[#B23A2B] text-white" : "border-[#A8722A]/30 text-[#6B5044] hover:border-[#A8722A]"}`}>
              {t}
            </button>
          ))}
        </div>
      </section>

      {RITUAL_CATEGORIES.map((cat) => {
        const items = filtered(cat);
        if (!items.length) return null;
        return (
          <section key={cat} className="mx-auto max-w-6xl px-4 py-8 md:px-8">
            <h2 className="font-eczar text-2xl font-semibold">{cat}</h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((r) => (
                <Link key={r.slug} to={`/rituals/${r.slug}`} data-testid={`ritual-card-${r.slug}`}
                  className="group flex flex-col rounded-[16px] border border-[#A8722A]/20 bg-[#FFFDF8] p-5 shadow-sm transition-shadow hover:shadow-md">
                  <BookOpen size={20} className="text-[#B23A2B]" />
                  <p className="mt-3 font-baloo text-lg text-[#3B221B]">{r.title.kn}</p>
                  <h3 className="font-eczar text-lg font-semibold">{r.title.en}</h3>
                  <p className="mt-1 flex-1 font-mukta text-sm text-[#6B5044]">{r.summary.en}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {r.tradition.map((t) => (
                      <span key={t} className={`rounded-full border px-2 py-0.5 font-mukta text-[11px] ${TRAD_COLORS}`}>{t}</span>
                    ))}
                  </div>
                  <span className="mt-3 inline-flex items-center gap-1 font-mukta text-sm font-semibold text-[#B23A2B] group-hover:gap-2">
                    Learn the steps <ArrowRight size={15} />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
      <SiteFooter />
    </div>
  );
}
